import * as THREE from 'three';
import {
  LINING,
  WORN,
  box,
  cylinderN,
  facetedDisc,
  facetedLoft,
  mergeParts,
  part,
  ringFrontZ,
  shellLoft,
  taperedBox,
  wedge,
  type FacetedLoftRing,
  type Part,
} from '@/art/geometry';
import { vertexColored } from '@/art/materials';
import type { PaletteName } from '@/art/palette';
import type { CharacterSpec, CoatStyle, HairStyle } from './spec';

/**
 * Bouwt een personage als gesegmenteerde rig.
 *
 * Geen skinning: elk gewricht is een `Object3D`-pivot die exact op zijn gewrichtshoogte
 * staat, met daaronder één samengevoegde mesh per lichaamsdeel. Animatie is daardoor niets
 * meer dan het roteren van pivots (zie `animator.ts`).
 *
 * De opbouw is anatomie-eerst: één lichaamsenvelop (`bodyEnvelope`) legt per hoogte de
 * breedte en diepte van de romp vast — heup, taille-insnoering, ribbenkast, borst,
 * schouderlijn — en alles wat daarover heen hangt (shirt, jas, riem) wordt uit diezelfde
 * envelop afgeleid met een vaste ruimte eromheen. Zo kan een shirt nooit door een jas
 * prikken en leest een dikke havenjas als stof over hetzelfde lichaam als een dun colbert.
 *
 * Elk gewricht krijgt een kogel op de ouderkant (elleboog op de bovenarm, knie op het
 * dijbeen), zodat er bij het roteren van het kind geen gat openvalt.
 *
 * Alle maten staan in de tabellen hieronder; de builder-functies bevatten alleen
 * verhoudingen ten opzichte van die tabellen en de `CharacterProportions` uit de spec.
 */

export const JOINT_NAMES = [
  'pelvis',
  'torso',
  'neck',
  'head',
  'shoulderL',
  'elbowL',
  'shoulderR',
  'elbowR',
  'hipL',
  'kneeL',
  'hipR',
  'kneeR',
] as const;

export type JointName = (typeof JOINT_NAMES)[number];

export interface CharacterRig {
  /** Wortel op de grond tussen de voeten. Verplaats dit object om het personage te bewegen. */
  readonly root: THREE.Group;
  readonly joints: Readonly<Record<JointName, THREE.Object3D>>;
  readonly spec: CharacterSpec;
  readonly deformation: 'segmented' | 'skinned';
}

/** +1 is de linkerzijde van het personage (+X), −1 de rechter. */
type Side = 1 | -1;
const SIDES_LR: readonly Side[] = [1, -1];

// ---------------------------------------------------------------------------
// Parametertabellen
// ---------------------------------------------------------------------------

/**
 * Facetten per volume. Meer leest niet meer als low poly; minder leest als buis. Het hoofd
 * krijgt het meest omdat een kaaklijn in 12 facetten een doos wordt.
 */
const FACETS = {
  torso: 20,
  head: 24,
  hair: 24,
  hat: 28,
  limb: 12,
  joint: 10,
  hand: 8,
  shoe: 12,
} as const;

/**
 * Romp-opbouw, in meters relatief aan de heup-pivot (y = 0 op `hipHeight`).
 *
 * De envelop-hoogtes zijn de sleutelringen van het lichaam; alles ertussen wordt lineair
 * geïnterpoleerd. `chestFraction` en `deltoid` bepalen samen de schouderlijn: de borst is
 * smaller dan de schouders, en de deltoïde-kap vult dat verschil als een schuine massa in
 * plaats van een plank.
 */
const TORSO = {
  crotchDrop: 0.14,
  hipY: -0.04,
  beltY: 0.05,
  beltHeight: 0.045,
  waistY: 0.13,
  ribsY: 0.25,
  chestY: 0.35,
  /** Borstbreedte als fractie van de schouderbreedte. */
  chestFraction: 0.78,
  /** Deltoïde-kap op de schouder, in meters. */
  deltoid: { width: 0.158, depth: 0.18, height: 0.13 },
  /** Hoe ver de jas-schouderlijn onder de schouder-pivot begint. */
  shoulderLine: 0.03,
} as const;

/**
 * Jasstijlen. `hemDrop` is de zoom relatief aan de heup-pivot; negatief hangt onder de
 * heup. `openGap` is de halve spleethoek aan de voorkant [zoom, borst] bij een open jas —
 * groter bovenaan geeft de V-hals waar het shirt en de hals doorheen komen.
 */
interface CoatParams {
  readonly hemDrop: number;
  readonly thickness: number;
  readonly ease: number;
  readonly hemFlare: number;
  readonly collar: 'stand' | 'lapel' | 'wide' | 'closed';
  readonly waistband: boolean;
  readonly ribbedCuffs: boolean;
  readonly belt: boolean;
  readonly buttons: number;
  readonly buttonColumns: 1 | 2;
  readonly epaulettes: boolean;
  /** Messing knopen (uniform) in plaats van stofkleurige. */
  readonly brassButtons: boolean;
  readonly openGap: readonly [number, number];
}

const COATS: Readonly<Record<CoatStyle, CoatParams>> = {
  blouson: {
    hemDrop: 0.06,
    thickness: 0.018,
    ease: 0.028,
    hemFlare: -0.02,
    collar: 'stand',
    waistband: true,
    ribbedCuffs: true,
    belt: false,
    buttons: 0,
    buttonColumns: 1,
    epaulettes: false,
    brassButtons: false,
    openGap: [0.22, 0.42],
  },
  sportsCoat: {
    hemDrop: -0.12,
    thickness: 0.016,
    ease: 0.032,
    hemFlare: 0.012,
    collar: 'lapel',
    waistband: false,
    ribbedCuffs: false,
    belt: false,
    buttons: 2,
    buttonColumns: 1,
    epaulettes: false,
    brassButtons: false,
    openGap: [0.28, 0.5],
  },
  overcoat: {
    hemDrop: -0.34,
    thickness: 0.022,
    ease: 0.038,
    hemFlare: 0.045,
    collar: 'lapel',
    waistband: false,
    ribbedCuffs: false,
    belt: false,
    buttons: 3,
    buttonColumns: 1,
    epaulettes: false,
    brassButtons: false,
    openGap: [0.48, 0.5],
  },
  peacoat: {
    hemDrop: -0.1,
    thickness: 0.03,
    ease: 0.05,
    hemFlare: 0.005,
    collar: 'wide',
    waistband: false,
    ribbedCuffs: false,
    belt: false,
    buttons: 3,
    buttonColumns: 2,
    epaulettes: false,
    brassButtons: false,
    openGap: [0.2, 0.36],
  },
  tunic: {
    hemDrop: -0.14,
    thickness: 0.015,
    ease: 0.026,
    hemFlare: 0.01,
    collar: 'closed',
    waistband: false,
    ribbedCuffs: false,
    belt: true,
    buttons: 5,
    buttonColumns: 1,
    epaulettes: true,
    brassButtons: true,
    openGap: [0, 0],
  },
};

/**
 * Gezichtsverhoudingen als fractie van de hoofdhoogte, gemeten vanaf de kin.
 *
 * Deze tabel is het hele verschil tussen een gezicht en een doos met strepen. Een snor op
 * 0.36 zit op de neus; ogen op 0.66 zitten in de wenkbrauwen. Plaats nooit een
 * gezichtsonderdeel op een los verzonnen hoogte — gebruik deze fracties.
 */
const FACE = {
  mouth: 0.24,
  lowerLip: 0.205,
  upperLip: 0.275,
  moustache: 0.315,
  noseBase: 0.4,
  noseBridge: 0.62,
  cheek: 0.4,
  eyes: 0.565,
  earCentre: 0.5,
  brow: 0.67,
  eyebrow: 0.7,
  forehead: 0.84,
  /** Onderkant van de haarband aan de slapen: net boven het oor. */
  templeHair: 0.6,
} as const;

/** Schedelringen: per hoogte breedte (×hw), diepte (×hd), voorwaartse offset (×hd), vorm. */
const SKULL = [
  { f: 0.0, w: 0.52, d: 0.48, z: 0.11, sq: 0.35, bias: 0.5, jaw: true },
  { f: 0.14, w: 1.0, d: 0.82, z: 0.05, sq: 0.55, bias: 0.48, jaw: true },
  // Jukbeenring: breed en vlak van voren, zodat er een wangvlak ontstaat.
  { f: 0.4, w: 1.04, d: 0.98, z: 0.02, sq: 0.5, bias: 0.47, jaw: false },
  // Oogring ligt met een lagere frontBias achter wang en wenkbrauwboog: de oogkas.
  { f: 0.565, w: 1.02, d: 1.0, z: 0.0, sq: 0.4, bias: 0.41, jaw: false },
  { f: 0.68, w: 1.0, d: 1.0, z: 0.01, sq: 0.4, bias: 0.47, jaw: false },
  { f: 0.84, w: 0.92, d: 0.94, z: -0.02, sq: 0.3, bias: 0.47, jaw: false },
  { f: 1.0, w: 0.5, d: 0.52, z: -0.06, sq: 0.1, bias: 0.5, jaw: false },
] as const;

/**
 * Rusthouding die in de geometrie zit, niet in de animator: de onderarm hangt iets naar
 * voren (elleboog nooit op slot) en de hand volgt. De animator zet rotaties absoluut, dus
 * dit is de enige plek waar een ontspannen arm vandaan kan komen.
 */
const REST = {
  forearmDrift: 0.028,
  elbowOut: 0.006,
} as const;

/** Haardrachten: haarlijn, dikte-schaal, en hoe ver de zijkanten omlaag lopen. */
interface HairParams {
  readonly hairline: number;
  readonly thickness: number;
  readonly sidesFrom: number;
  readonly sideGap: number;
  readonly cap: boolean;
  readonly quiff: boolean;
}

const HAIR: Readonly<Record<HairStyle, HairParams>> = {
  slickBack: { hairline: 0.76, thickness: 1.0, sidesFrom: 0.6, sideGap: 0.95, cap: true, quiff: true },
  crop: { hairline: 0.8, thickness: 0.6, sidesFrom: 0.62, sideGap: 1.0, cap: true, quiff: false },
  curly: { hairline: 0.72, thickness: 1.7, sidesFrom: 0.56, sideGap: 0.85, cap: true, quiff: false },
  balding: { hairline: 0.86, thickness: 0.7, sidesFrom: 0.58, sideGap: 1.15, cap: false, quiff: false },
  bald: { hairline: 1, thickness: 0, sidesFrom: 1, sideGap: 0, cap: false, quiff: false },
};

/** Hoogte waarop een hoofddeksel op de schedel rust, als fractie van de hoofdhoogte. */
const HAT_BASE = 0.85;

// ---------------------------------------------------------------------------
// Rig
// ---------------------------------------------------------------------------

export function buildCharacter(spec: CharacterSpec): CharacterRig {
  const p = spec.proportions;

  const root = new THREE.Group();
  root.name = `NA_Char_${spec.name}`;

  const joints = {} as Record<JointName, THREE.Object3D>;

  const pivot = (name: JointName, parent: THREE.Object3D, x: number, y: number, z: number) => {
    const object = new THREE.Object3D();
    object.name = name;
    object.position.set(x, y, z);
    parent.add(object);
    joints[name] = object;
    return object;
  };

  const attach = (parent: THREE.Object3D, parts: Part[], name: string) => {
    const mesh = new THREE.Mesh(mergeParts(parts), vertexColored());
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  };

  // --- Romp -----------------------------------------------------------------
  const pelvis = pivot('pelvis', root, 0, p.hipHeight, 0);
  const torso = pivot('torso', pelvis, 0, 0, 0);
  attach(torso, torsoParts(spec), 'torso');

  const neck = pivot('neck', torso, 0, p.neckHeight - p.hipHeight, 0);
  const head = pivot('head', neck, 0, 0, 0);
  attach(head, headParts(spec), 'head');

  // --- Armen ----------------------------------------------------------------
  for (const side of SIDES_LR) {
    const suffix = side === 1 ? 'L' : 'R';
    const shoulder = pivot(
      `shoulder${suffix}` as JointName,
      torso,
      side * armPivotX(spec),
      p.shoulderHeight - p.hipHeight,
      0,
    );
    attach(shoulder, upperArmParts(spec, side), `upperArm${suffix}`);

    const elbow = pivot(`elbow${suffix}` as JointName, shoulder, 0, -(p.shoulderHeight - p.elbowHeight), 0);
    attach(elbow, foreArmParts(spec, side), `foreArm${suffix}`);
  }

  // --- Benen ----------------------------------------------------------------
  for (const side of SIDES_LR) {
    const suffix = side === 1 ? 'L' : 'R';
    const hip = pivot(`hip${suffix}` as JointName, pelvis, side * hipPivotX(spec), 0, 0);
    attach(hip, thighParts(spec, side), `thigh${suffix}`);

    const knee = pivot(`knee${suffix}` as JointName, hip, 0, -(p.hipHeight - p.kneeHeight), 0);
    attach(knee, shinParts(spec, side), `shin${suffix}`);
  }

  return { root, joints, spec, deformation: 'segmented' };
}

/** X van de schouder-pivot: het schoudergewricht zit een halve armdikte binnen de schouderrand. */
function armPivotX(spec: CharacterSpec): number {
  return (spec.proportions.shoulderWidth - spec.proportions.armThickness) / 2;
}

/** X van de heup-pivot: de benen hangen een halve beendikte binnen de heuprand. */
function hipPivotX(spec: CharacterSpec): number {
  return (spec.proportions.hipWidth - spec.proportions.legThickness) / 2;
}

// ---------------------------------------------------------------------------
// Lichaamsenvelop
// ---------------------------------------------------------------------------

/**
 * Sleutelringen van de romp, van kruis tot nekbasis, in de lokale ruimte van de pelvis.
 * Dit is het naakte lichaam; kleding wordt hieruit afgeleid.
 */
function bodyEnvelope(spec: CharacterSpec): FacetedLoftRing[] {
  const p = spec.proportions;
  const torsoH = p.shoulderHeight - p.hipHeight;
  const neckY = p.neckHeight - p.hipHeight;
  const chestW = p.shoulderWidth * TORSO.chestFraction;
  const d = p.torsoDepth;
  const belly = p.bellyDepth;

  return [
    { y: -0.4, width: p.hipWidth * 0.96, depth: d * 0.84, squareness: 0.5 },
    { y: -TORSO.crotchDrop, width: p.hipWidth * 0.98, depth: d * 0.86, squareness: 0.5 },
    { y: TORSO.hipY, width: p.hipWidth * 1.04, depth: d * 0.98, squareness: 0.5 },
    { y: TORSO.beltY, width: (p.hipWidth + p.waistWidth) / 2 + 0.01, depth: d * 0.92 + belly * 0.7, offsetZ: belly * 0.35, squareness: 0.45 },
    { y: TORSO.waistY, width: p.waistWidth, depth: d * 0.86 + belly, offsetZ: belly * 0.5, squareness: 0.42 },
    { y: TORSO.ribsY, width: (p.waistWidth + chestW) / 2, depth: d * 0.98 + belly * 0.45, offsetZ: belly * 0.22, squareness: 0.5, frontBias: 0.52 },
    { y: TORSO.chestY, width: chestW, depth: d * 1.02, offsetZ: 0.008, squareness: 0.6, frontBias: 0.55 },
    { y: torsoH - TORSO.shoulderLine, width: chestW * 0.98, depth: d * 0.92, offsetZ: -0.004, squareness: 0.55 },
    { y: neckY + 0.005, width: p.neckWidth * 1.9, depth: p.neckWidth * 1.6, offsetZ: -0.008, squareness: 0.3 },
  ];
}

/** Lineair geïnterpoleerde ring uit een gesorteerde ringenlijst. */
function ringAt(rings: readonly FacetedLoftRing[], y: number): FacetedLoftRing {
  if (y <= rings[0]!.y) return { ...rings[0]!, y };
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i]!;
    const b = rings[i + 1]!;
    if (y <= b.y) {
      const t = (y - a.y) / (b.y - a.y);
      const mix = (u: number | undefined, v: number | undefined, fallback: number) =>
        (u ?? fallback) + ((v ?? fallback) - (u ?? fallback)) * t;
      return {
        y,
        width: mix(a.width, b.width, 0),
        depth: mix(a.depth, b.depth, 0),
        offsetX: mix(a.offsetX, b.offsetX, 0),
        offsetZ: mix(a.offsetZ, b.offsetZ, 0),
        squareness: mix(a.squareness, b.squareness, 0),
        frontBias: mix(a.frontBias, b.frontBias, 0.5),
      };
    }
  }
  return { ...rings[rings.length - 1]!, y };
}

/** Ring met extra ruimte rondom (kleding over een lichaam). */
function inflate(ring: FacetedLoftRing, amount: number, extra: Partial<FacetedLoftRing> = {}): FacetedLoftRing {
  return { ...ring, width: ring.width + amount * 2, depth: ring.depth + amount * 2, ...extra };
}

// ---------------------------------------------------------------------------
// Romp
// ---------------------------------------------------------------------------

/**
 * De romp: broek-bekken, riem, shirt-lichaam, jas, kraag, schouders en accessoires.
 * Lokale ruimte: y = 0 is de heuphoogte (de pelvis-pivot).
 */
function torsoParts(spec: CharacterSpec): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const f = spec.features;
  const coat = COATS[f.coatStyle];
  const open = f.openCoat && coat.collar !== 'closed';
  const torsoH = p.shoulderHeight - p.hipHeight;
  const neckY = p.neckHeight - p.hipHeight;
  const body = bodyEnvelope(spec);
  const parts: Part[] = [];

  // --- Broek en bekken -------------------------------------------------------
  // Loopt door tot onder het kruis, zodat de twee dijbenen uit één bekken hangen in plaats
  // van als losse buizen aan een riem.
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.torso,
        rings: [
          inflate(ringAt(body, -TORSO.crotchDrop), 0.006),
          inflate(ringAt(body, TORSO.hipY), 0.008),
          inflate(ringAt(body, TORSO.beltY + 0.01), 0.008),
        ],
        capTop: false,
      }),
      c.trousers,
    ),
  );

  // --- Riem en gesp ----------------------------------------------------------
  // Bij een gesloten lange jas is de riem onzichtbaar; een tunic draagt zijn koppel óver de
  // jas. Beide gevallen hieronder.
  if (!coat.belt && (open || coat.hemDrop > TORSO.beltY)) {
    const beltRing = inflate(ringAt(body, TORSO.beltY + TORSO.beltHeight / 2), 0.012);
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.torso,
          rings: [
            { ...beltRing, y: TORSO.beltY },
            { ...beltRing, y: TORSO.beltY + TORSO.beltHeight },
          ],
        }),
        'charcoal',
      ),
    );
    parts.push(
      part(
        box(0.055, TORSO.beltHeight * 0.8, 0.014).translate(
          0,
          TORSO.beltY + TORSO.beltHeight / 2,
          ringFrontZ(beltRing, 0) + 0.004,
        ),
        'steelBuckle',
      ),
    );
  }

  // --- Shirt-lichaam -----------------------------------------------------------
  // Het lichaam zelf, in shirtkleur, van net onder de riem tot de nekbasis. Bij een open
  // jas is dit wat je in de V ziet; bij een dichte jas blijft alleen de kraag over.
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.torso,
        rings: [
          inflate(ringAt(body, TORSO.beltY - 0.01), 0.004),
          inflate(ringAt(body, TORSO.waistY), 0.004),
          inflate(ringAt(body, TORSO.ribsY), 0.004),
          inflate(ringAt(body, TORSO.chestY), 0.004),
          inflate(ringAt(body, torsoH - TORSO.shoulderLine), 0.004),
          ringAt(body, neckY + 0.005),
        ],
        capBottom: false,
      }),
      c.shirt,
    ),
  );

  // --- Jas -------------------------------------------------------------------
  parts.push(...coatParts(spec, body, coat, open));

  // --- Schouders -------------------------------------------------------------
  // Deltoïde-kappen: een schuine massa die van de nekbasis naar de bovenarm afloopt en de
  // bovenarm-loft ruim overlapt. Zonder dit leest de schouder als een hanger.
  const pivotX = armPivotX(spec);
  const dw = TORSO.deltoid.width + coat.thickness * 2;
  const dd = TORSO.deltoid.depth + coat.thickness * 2;
  const dh = TORSO.deltoid.height;
  for (const side of SIDES_LR) {
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.limb,
          // Laag profiel: de kap loopt vanuit de mouw omhoog en naar binnen de trapezius in,
          // zonder boven de schouderlijn uit te bollen — anders leest hij als epaulet.
          rings: [
            { y: torsoH - dh * 0.7, width: dw * 0.9, depth: dd * 0.9, offsetX: side * (pivotX + 0.004), squareness: 0.3 },
            { y: torsoH - dh * 0.3, width: dw, depth: dd, offsetX: side * pivotX, squareness: 0.32 },
            { y: torsoH + dh * 0.05, width: dw * 0.86, depth: dd * 0.9, offsetX: side * (pivotX - 0.016), squareness: 0.32 },
            { y: torsoH + dh * 0.2, width: dw * 0.55, depth: dd * 0.7, offsetX: side * (pivotX - 0.048), squareness: 0.25 },
          ],
        }),
        c.coat,
      ),
    );
    if (coat.epaulettes) {
      const strap = box(0.04, 0.012, 0.13);
      strap.rotateZ(side * 0.22);
      strap.translate(side * (pivotX - 0.035), torsoH + dh * 0.2, -0.005);
      parts.push(part(strap, c.coat, WORN));
    }
  }

  // --- Kraag en hals -----------------------------------------------------------
  parts.push(...collarParts(spec, body, coat, open));

  if (open && f.chain) {
    const chestZ = ringFrontZ(ringAt(body, torsoH - 0.06), 0);
    for (const side of SIDES_LR) {
      const strand = box(0.06, 0.008, 0.008);
      strand.rotateZ(side * -0.7);
      strand.translate(side * 0.022, torsoH - 0.075, chestZ + 0.006);
      parts.push(part(strand, c.accent));
    }
    parts.push(part(box(0.022, 0.03, 0.01).translate(0, torsoH - 0.125, chestZ + 0.008), c.accent));
  }

  return parts;
}

/** De jas als schaal met voering, plus knopen, tailleband, koppel en badge. */
function coatParts(spec: CharacterSpec, body: readonly FacetedLoftRing[], coat: CoatParams, open: boolean): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const torsoH = p.shoulderHeight - p.hipHeight;
  const parts: Part[] = [];

  const top = torsoH - TORSO.shoulderLine + 0.01;
  const hem = coat.hemDrop;
  const levels = [hem, hem + 0.06, TORSO.hipY, TORSO.beltY, TORSO.waistY, TORSO.ribsY, TORSO.chestY, top].filter(
    (y, index, all) => y >= hem && y <= top && (index === 0 || y - all[index - 1]! > 0.025),
  );
  // Zoomverbreding loopt lineair uit van de heup naar de zoom.
  const flareAt = (y: number) => (y < TORSO.hipY ? (coat.hemFlare * (TORSO.hipY - y)) / (TORSO.hipY - hem || 1) : 0);
  const gapAt = (y: number) => {
    if (!open) return 0;
    const t = Math.min(1, Math.max(0, (y - hem) / (TORSO.chestY - hem)));
    return coat.openGap[0] + (coat.openGap[1] - coat.openGap[0]) * t;
  };
  const rings = levels.map((y) => ({
    ...inflate(ringAt(body, y), coat.ease + flareAt(y)),
    gap: gapAt(y),
  }));

  const shell = shellLoft({
    sides: FACETS.torso,
    thickness: coat.thickness,
    gap: open ? coat.openGap[0] : 0,
    rings,
  });
  parts.push(part(shell.outer, c.coat));
  parts.push(part(shell.inner, c.coat, LINING));
  parts.push(part(shell.rim, c.coat));

  if (coat.waistband) {
    const band = inflate(ringAt(body, hem + 0.02), coat.ease - 0.004);
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.torso,
          gap: open ? coat.openGap[0] * 0.9 : 0,
          rings: [
            { ...band, y: hem - 0.005 },
            { ...band, y: hem + 0.04 },
          ],
        }),
        c.coat,
        LINING,
      ),
    );
  }

  if (coat.belt) {
    const beltRing = inflate(ringAt(body, TORSO.waistY), coat.ease + coat.thickness + 0.004);
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.torso,
          rings: [
            { ...beltRing, y: TORSO.waistY - 0.02 },
            { ...beltRing, y: TORSO.waistY + 0.025 },
          ],
        }),
        'charcoal',
      ),
    );
    parts.push(part(box(0.05, 0.04, 0.012).translate(0, TORSO.waistY + 0.002, ringFrontZ(beltRing, 0) + 0.004), c.accent));
  }

  // Zakkleppen op de heup: het kleinste detail dat een jas als kleermakerswerk laat lezen.
  if (coat.collar === 'lapel' || coat.collar === 'wide') {
    const pocketY = Math.max(hem + 0.07, TORSO.hipY + 0.02);
    const ring = inflate(ringAt(body, pocketY), coat.ease + coat.thickness + flareAt(pocketY));
    for (const side of SIDES_LR) {
      const x = side * ring.width * 0.3;
      const flap = box(0.085, 0.022, 0.012);
      flap.rotateY(-side * 0.5);
      flap.translate(x, pocketY, ringFrontZ(ring, x) + 0.004);
      parts.push(part(flap, c.coat, WORN));
    }
  }

  if (spec.features.tie && coat.collar === 'lapel') {
    parts.push(...tieParts(spec, body, coat, open));
  }

  if (!open && coat.buttons > 0) {
    const from = Math.max(hem + 0.05, TORSO.beltY + 0.02);
    const to = TORSO.chestY + 0.04;
    const columns = coat.buttonColumns === 2 ? [-0.045, 0.045] : [0];
    for (let i = 0; i < coat.buttons; i++) {
      const y = from + ((to - from) * (i + 0.5)) / coat.buttons;
      const ring = inflate(ringAt(body, y), coat.ease + coat.thickness);
      for (const x of columns) {
        const button = facetedDisc(0.02, 0.02, 6);
        button.rotateX(Math.PI / 2);
        button.translate(x, y, ringFrontZ(ring, x) + 0.004);
        parts.push(part(button, coat.brassButtons ? c.accent : c.coat, coat.brassButtons ? 1 : LINING));
      }
    }
  }

  return parts;
}

/**
 * Stropdas: knoop onder de shirtkraag, blad langs de borst omlaag tot de sluiting van de
 * jas (dicht) of tot boven de riem (open). Volgt de borstringen, dus hij zweeft nooit.
 */
function tieParts(spec: CharacterSpec, body: readonly FacetedLoftRing[], coat: CoatParams, open: boolean): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const torsoH = p.shoulderHeight - p.hipHeight;
  const parts: Part[] = [];
  const top = torsoH - 0.035;
  const bottom = open || coat.buttons === 0 ? TORSO.beltY + 0.1 : TORSO.chestY + 0.045;
  const surface = (y: number) => ringFrontZ(inflate(ringAt(body, y), 0.004), 0);

  const knot = box(0.03, 0.03, 0.02);
  knot.translate(0, top + 0.01, surface(top) + 0.01);
  parts.push(part(knot, c.accent));

  // Blad in twee stukken zodat hij de borstwelving volgt.
  const mid = (top + bottom) / 2;
  for (const [y0, y1, w0, w1] of [
    [top, mid, 0.026, 0.038],
    [mid, bottom, 0.038, 0.048],
  ] as const) {
    const blade = taperedBox({ bottomWidth: w1, topWidth: w0, height: y0 - y1, bottomDepth: 0.008, topDepth: 0.008 });
    const z0 = surface(y0) + 0.006;
    const z1 = surface(y1) + 0.006;
    blade.rotateX(Math.atan2(z1 - z0, y0 - y1));
    blade.translate(0, (y0 + y1) / 2, (z0 + z1) / 2);
    parts.push(part(blade, c.accent));
  }
  return parts;
}

/** Kraag per jasstijl, plus shirtkraag en de blote V-hals bij een open jas. */
function collarParts(spec: CharacterSpec, body: readonly FacetedLoftRing[], coat: CoatParams, open: boolean): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const torsoH = p.shoulderHeight - p.hipHeight;
  const neckY = p.neckHeight - p.hipHeight;
  const nw = p.neckWidth;
  const parts: Part[] = [];
  const chest = ringAt(body, torsoH - 0.05);
  const chestFront = ringFrontZ(chest, 0);

  const neckRing = (y: number, scale: number, back: number): FacetedLoftRing => ({
    y,
    width: nw * 1.55 * scale,
    depth: nw * 1.45 * scale,
    offsetZ: -back,
    squareness: 0.35,
  });

  switch (coat.collar) {
    case 'stand': {
      const shell = shellLoft({
        sides: FACETS.limb,
        thickness: 0.012,
        gap: 0.7,
        rings: [neckRing(torsoH - 0.012, 1.08, 0.006), neckRing(torsoH + 0.045, 1.22, 0.018)],
      });
      parts.push(part(shell.outer, c.coat), part(shell.inner, c.coat, LINING), part(shell.rim, c.coat));
      for (const side of SIDES_LR) {
        const lapel = taperedBox({ bottomWidth: 0.035, topWidth: 0.095, height: 0.11, bottomDepth: 0.012, topDepth: 0.012 });
        lapel.rotateZ(side * 0.62);
        lapel.rotateX(-0.42);
        lapel.translate(side * 0.078, torsoH - 0.05, chestFront + 0.024);
        parts.push(part(lapel, c.coat, WORN));
      }
      break;
    }
    case 'lapel': {
      const shell = shellLoft({
        sides: FACETS.limb,
        thickness: 0.012,
        gap: 1.05,
        rings: [neckRing(torsoH - 0.01, 1.12, 0.008), neckRing(torsoH + 0.04, 1.28, 0.022)],
      });
      parts.push(part(shell.outer, c.coat), part(shell.inner, c.coat, LINING), part(shell.rim, c.coat));
      for (const side of SIDES_LR) {
        const lapel = taperedBox({ bottomWidth: 0.028, topWidth: 0.1, height: 0.24, bottomDepth: 0.014, topDepth: 0.014 });
        lapel.rotateZ(side * 0.3);
        lapel.rotateX(-0.12);
        lapel.translate(side * 0.068, TORSO.chestY - 0.02, ringFrontZ(ringAt(body, TORSO.chestY), 0.06) + coat.ease + 0.02);
        parts.push(part(lapel, c.coat, WORN));
      }
      break;
    }
    case 'wide': {
      const shell = shellLoft({
        sides: FACETS.limb,
        thickness: 0.02,
        gap: 0.62,
        rings: [neckRing(torsoH - 0.02, 1.5, 0.012), neckRing(torsoH + 0.08, 1.95, 0.034)],
      });
      parts.push(part(shell.outer, c.coat), part(shell.inner, c.coat, LINING), part(shell.rim, c.coat));
      break;
    }
    case 'closed': {
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.limb,
            rings: [neckRing(torsoH - 0.012, 1.02, 0.004), neckRing(torsoH + 0.038, 1.06, 0.008)],
            capBottom: false,
            capTop: false,
          }),
          c.coat,
        ),
      );
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.limb,
            rings: [neckRing(torsoH + 0.03, 0.92, 0.004), neckRing(torsoH + 0.052, 0.94, 0.006)],
            capBottom: false,
            capTop: false,
          }),
          c.shirt,
        ),
      );
      break;
    }
  }

  if (open) {
    // Blote hals-en-borstdriehoek tussen de shirtkraag. Zonder dit leest het bovenlijf als
    // één dichte massa en verdwijnt het gezicht in de romp.
    parts.push(part(box(0.062, 0.11, 0.024).translate(0, torsoH - 0.045, chestFront - 0.002), c.skin));
    for (const side of SIDES_LR) {
      const shirtCollar = taperedBox({ bottomWidth: 0.03, topWidth: 0.06, height: 0.075, bottomDepth: 0.012, topDepth: 0.012 });
      shirtCollar.rotateZ(side * 0.36);
      shirtCollar.rotateX(-0.25);
      shirtCollar.translate(side * 0.048, torsoH - 0.018, chestFront + 0.008);
      parts.push(part(shirtCollar, c.shirt));
    }
  } else if (coat.collar !== 'closed') {
    // Dichte jas: alleen de shirtkraag piept boven de revers uit.
    for (const side of SIDES_LR) {
      const shirtCollar = taperedBox({ bottomWidth: 0.03, topWidth: 0.05, height: 0.05, bottomDepth: 0.012, topDepth: 0.012 });
      shirtCollar.rotateZ(side * 0.34);
      shirtCollar.translate(side * 0.04, neckY - 0.005, chestFront + 0.004);
      parts.push(part(shirtCollar, c.shirt));
    }
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Hoofd
// ---------------------------------------------------------------------------

/**
 * Het hoofd: hals, schedel in twee delen (kaak en schedel), gezicht, oren, haar of
 * hoofddeksel, en de accessoires.
 *
 * Lokale ruimte: y = 0 is de nekhoogte (de neck-pivot). Alle gezichtsposities lopen via
 * `faceY()` en `faceFront()`, zodat een oog altijd óp het schedeloppervlak zit — ook als
 * de kaakbreedte of de neusgrootte per personage verandert.
 */
function headParts(spec: CharacterSpec): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const f = spec.features;
  const parts: Part[] = [];

  const hw = p.headWidth;
  const hd = p.headDepth;
  const hh = p.crownHeight - p.chinHeight;
  const chinY = p.chinHeight - p.neckHeight;
  const faceY = (fraction: number): number => chinY + hh * fraction;

  const skull: FacetedLoftRing[] = SKULL.map((r) => ({
    y: faceY(r.f),
    width: hw * r.w * (r.jaw ? p.jawWidth : 1),
    depth: hd * r.d,
    offsetZ: hd * r.z,
    squareness: r.jaw ? Math.min(1, r.sq * 0.4 + p.jawSquareness * 0.6) : r.sq,
    frontBias: r.bias,
  }));
  const skullAt = (fraction: number) => ringAt(skull, faceY(fraction));
  const faceFront = (fraction: number, x = 0) => ringFrontZ(skullAt(fraction), x);

  // --- Hals ------------------------------------------------------------------
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.limb,
        rings: [
          { y: -0.07, width: p.neckWidth * 1.14, depth: p.neckWidth * 1.14, squareness: 0.3 },
          { y: 0.0, width: p.neckWidth, depth: p.neckWidth * 1.04, squareness: 0.3 },
          { y: chinY + 0.03, width: p.neckWidth * 0.98, depth: p.neckWidth * 1.02, offsetZ: hd * 0.03, squareness: 0.3 },
        ],
      }),
      c.skin,
    ),
  );

  // --- Kaak en schedel ---------------------------------------------------------
  // Twee lofts met een gedeelde ring op jukbeenhoogte: de kaak krijgt bij stoppels of een
  // baard zijn eigen kleur zonder een tweede vorm eroverheen te plakken.
  const jawRings = skull.filter((_, i) => SKULL[i]!.jaw || i === 2);
  const craniumRings = skull.filter((_, i) => !SKULL[i]!.jaw);
  const beard = f.facialHair === 'fullBeard';
  const jawColor: PaletteName = beard ? c.hair : f.facialHair === 'stubble' ? 'stubble' : c.skin;
  const skullPhase = Math.PI / FACETS.head;
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.head,
        phase: skullPhase,
        rings: beard
          ? [
              { ...inflate(jawRings[0]!, 0.012), y: jawRings[0]!.y - hh * 0.07, offsetZ: hd * 0.08 },
              ...jawRings.map((r, i) => inflate(r, i === jawRings.length - 1 ? 0 : 0.012)),
            ]
          : jawRings,
        capTop: false,
      }),
      jawColor,
    ),
  );
  parts.push(part(facetedLoft({ sides: FACETS.head, phase: skullPhase, rings: craniumRings, capBottom: false }), c.skin));

  // --- Wenkbrauwboog, ogen, neus, mond ---------------------------------------
  const browZ = faceFront(FACE.brow);
  parts.push(
    part(
      wedge({ backWidth: hw * 0.86, frontWidth: hw * 0.76, height: hh * 0.055, depth: hd * 0.09, frontHeight: hh * 0.03 }).translate(
        0,
        faceY(FACE.brow),
        browZ - hd * 0.03,
      ),
      c.skin,
    ),
  );

  const eyeX = hw * 0.2;
  const eyeY = faceY(FACE.eyes);
  const eyeZ = faceFront(FACE.eyes, eyeX);
  for (const side of SIDES_LR) {
    // Oogkas: een donkere schijf in de teruggelegde oogring, met daarop wit en iris. De
    // schaduwrand boven het oog is het ooglid — zonder die rand staart een gezicht.
    const socket = facetedDisc(hw * 0.27, hh * 0.115, 8, 0.25);
    socket.rotateX(Math.PI / 2);
    socket.translate(side * eyeX, eyeY - hh * 0.005, eyeZ + 0.001);
    parts.push(part(socket, 'skinShadow'));

    const white = facetedDisc(hw * 0.17, hh * 0.062, 8, 0.15);
    white.rotateX(Math.PI / 2);
    white.translate(side * eyeX, eyeY - hh * 0.006, eyeZ + 0.003);
    parts.push(part(white, 'cream'));

    const iris = facetedDisc(hw * 0.085, hh * 0.058, 6);
    iris.rotateX(Math.PI / 2);
    iris.translate(side * (eyeX + hw * 0.006), eyeY - hh * 0.008, eyeZ + 0.005);
    parts.push(part(iris, 'lensDark'));

    parts.push(part(box(hw * 0.19, hh * 0.016, hd * 0.02).translate(side * eyeX, eyeY + hh * 0.026, eyeZ + 0.0055), 'skinShadow'));
    // Bovenste ooglid: huidrand die het oog van boven afdekt en de oogkas sluit.
    parts.push(part(box(hw * 0.22, hh * 0.024, hd * 0.035).translate(side * eyeX, eyeY + hh * 0.045, eyeZ + 0.004), c.skin));

    const eyebrow = box(hw * 0.3, hh * 0.03, hd * 0.035);
    eyebrow.rotateZ(side * -0.14);
    eyebrow.translate(side * (eyeX + hw * 0.02), faceY(FACE.eyebrow), faceFront(FACE.eyebrow, eyeX) + 0.006);
    parts.push(part(eyebrow, c.hair === 'hairGrey' ? 'hairGrey' : c.hair));
  }

  const ns = p.noseSize;
  const noseHeight = hh * (FACE.noseBridge - FACE.noseBase);
  const noseCentre = faceY((FACE.noseBridge + FACE.noseBase) / 2);
  parts.push(
    part(
      taperedBox({
        bottomWidth: hw * 0.2 * ns,
        topWidth: hw * 0.09 * ns,
        height: noseHeight,
        bottomDepth: hd * 0.15 * ns,
        topDepth: hd * 0.07,
        bottomOffsetZ: hd * 0.035 * ns,
      }).translate(0, noseCentre, faceFront(FACE.noseBase) - hd * 0.005),
      c.skin,
    ),
  );
  // Neusvleugels: een bredere, lage massa aan de basis. Zonder dit is de neus een spie.
  parts.push(
    part(
      wedge({ backWidth: hw * 0.3 * ns, frontWidth: hw * 0.2 * ns, height: hh * 0.055, depth: hd * 0.1 * ns, frontHeight: hh * 0.035 }).translate(
        0,
        faceY(FACE.noseBase) + hh * 0.02,
        faceFront(FACE.noseBase) + hd * 0.02 * ns,
      ),
      c.skin,
    ),
  );

  const mouthZ = faceFront(FACE.mouth);
  parts.push(part(box(hw * 0.25, hh * 0.04, hd * 0.03).translate(0, faceY(FACE.upperLip), mouthZ + 0.001), c.skin));
  parts.push(part(box(hw * 0.19, hh * 0.04, hd * 0.035).translate(0, faceY(FACE.lowerLip), mouthZ + 0.002), c.skin));
  parts.push(part(box(hw * 0.26, hh * 0.013, hd * 0.02).translate(0, faceY(FACE.mouth), mouthZ + 0.0085), 'skinShadow'));

  // --- Oren ------------------------------------------------------------------
  const earRing = skullAt(FACE.earCentre);
  for (const side of SIDES_LR) {
    const ear = facetedLoft({
      sides: 6,
      rings: [
        { y: 0, width: hh * 0.17, depth: hd * 0.1, squareness: 0.3 },
        { y: 0.012, width: hh * 0.15, depth: hd * 0.09, squareness: 0.3 },
      ],
    });
    ear.rotateZ(side * -Math.PI / 2);
    ear.translate(side * (earRing.width / 2 - 0.004), faceY(FACE.earCentre), (earRing.offsetZ ?? 0) - hd * 0.06);
    parts.push(part(ear, c.skin));
  }

  // --- Gezichtsbeharing ----------------------------------------------------
  if (f.facialHair === 'moustache' || f.facialHair === 'goatee' || beard) {
    parts.push(
      part(
        wedge({ backWidth: hw * 0.36, frontWidth: hw * 0.3, height: hh * 0.045, depth: hd * 0.055, frontHeight: hh * 0.03 }).translate(
          0,
          faceY(FACE.moustache),
          faceFront(FACE.moustache) - hd * 0.01,
        ),
        c.hair,
      ),
    );
  }
  if (f.facialHair === 'goatee') {
    parts.push(part(box(hw * 0.24, hh * 0.15, hd * 0.05).translate(0, faceY(0.11), faceFront(0.11) - hd * 0.008), c.hair));
    for (const side of SIDES_LR) {
      parts.push(part(box(hw * 0.055, hh * 0.19, hd * 0.045).translate(side * hw * 0.14, faceY(0.2), faceFront(0.2, hw * 0.14) - hd * 0.008), c.hair));
    }
  }

  // --- Haar en hoofddeksel -------------------------------------------------
  parts.push(...hairParts(spec, skull, faceY));
  if (f.hat !== 'none') parts.push(...hatParts(spec, skull, faceY));

  // --- Bril ------------------------------------------------------------------
  if (f.glasses) {
    // Een open montuur van vier dunne randen per glas, in schildpadbruin. Bewust géén
    // gevuld glas: je kijkt dwars door de glazen heen naar de ogen, en juist dat maakt hem
    // herkenbaar in plaats van een figuur met een donkere balk over zijn gezicht.
    const lensWidth = hw * 0.36;
    const lensHeight = hh * 0.17;
    const rim = hh * 0.028;
    const rimDepth = hd * 0.05;
    const z = eyeZ + hd * 0.055;
    const lensX = eyeX + hw * 0.01;
    for (const side of SIDES_LR) {
      const cx = side * lensX;
      parts.push(part(box(lensWidth, rim, rimDepth).translate(cx, eyeY + lensHeight / 2, z), 'tortoiseFrame'));
      parts.push(part(box(lensWidth, rim, rimDepth).translate(cx, eyeY - lensHeight / 2, z), 'tortoiseFrame'));
      for (const edge of SIDES_LR) {
        parts.push(part(box(rim, lensHeight, rimDepth).translate(cx + (edge * lensWidth) / 2, eyeY, z), 'tortoiseFrame'));
      }
      // Pootje langs de slaap, net bínnen het haar.
      parts.push(part(box(hw * 0.02, hh * 0.028, hd * 0.5).translate(side * (earRing.width / 2 - 0.002), eyeY + hh * 0.05, z - hd * 0.28), 'tortoiseFrame'));
    }
    parts.push(part(box(hw * 0.12, rim, rimDepth).translate(0, eyeY + hh * 0.035, z), 'tortoiseFrame'));
  }

  return parts;
}

/**
 * Haar: een band langs slapen en achterhoofd (open aan de voorkant, zodat het gezicht vrij
 * blijft) plus een kap over de kruin vanaf de haarlijn. Beide volgen de schedelringen met
 * een vaste dikte, dus het haar kan nooit zweven of de schedel doorsnijden.
 */
function hairParts(spec: CharacterSpec, skull: readonly FacetedLoftRing[], faceY: (f: number) => number): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const style = HAIR[spec.features.hair];
  const parts: Part[] = [];
  if (spec.features.hair === 'bald') return parts;

  const th = p.hairThickness * style.thickness;
  const hd = p.headDepth;
  const hw = p.headWidth;
  const skullAt = (fraction: number) => ringAt(skull, faceY(fraction));
  const hatted = spec.features.hat !== 'none';

  // Zijkanten en achterhoofd.
  const bandTop = style.cap ? style.hairline + 0.04 : Math.min(0.9, style.hairline + 0.02);
  const band = shellLoft({
    sides: FACETS.hair,
    thickness: Math.max(0.006, th * 0.45),
    gap: style.sideGap,
    rings: [
      { ...inflate(skullAt(style.sidesFrom), th * 0.35), squareness: 0.3 },
      { ...inflate(skullAt((style.sidesFrom + bandTop) / 2), th * 0.5), squareness: 0.3 },
      { ...inflate(skullAt(bandTop), th * 0.5), squareness: 0.3 },
    ],
  });
  parts.push(part(band.outer, c.hair), part(band.inner, c.hair, LINING), part(band.rim, c.hair));

  if (!style.cap || hatted) return parts;

  const hairline = skullAt(style.hairline);
  const crown = skullAt(1);
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.hair,
        rings: [
          { ...inflate(hairline, th * 0.4), squareness: 0.3 },
          { ...inflate(skullAt(0.9), th * 0.95), offsetZ: (skullAt(0.9).offsetZ ?? 0) - hd * 0.02, squareness: 0.3 },
          {
            y: crown.y + th * 0.25,
            width: hw * 0.92 + th * 2,
            depth: hd * 0.92 + th * 2,
            offsetZ: -hd * 0.07,
            squareness: 0.25,
          },
          { y: crown.y + th * 1.05, width: hw * 0.5, depth: hd * 0.6, offsetZ: -hd * 0.13, squareness: 0.1 },
        ],
        capBottom: false,
      }),
      c.hair,
    ),
  );

  if (spec.features.hair === 'curly') {
    // Krullen: een tweede, iets grotere ring halverwege de kap, verdraaid ten opzichte van
    // de eerste, zodat de facetten als plukken lezen in plaats van als gladde bol.
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.hair,
          phase: Math.PI / FACETS.hair,
          rings: [
            { ...inflate(skullAt(0.82), th * 0.7), squareness: 0.1 },
            { ...inflate(skullAt(0.93), th * 1.15), offsetZ: (skullAt(0.93).offsetZ ?? 0) - hd * 0.03, squareness: 0.05 },
            { y: crown.y + th * 0.9, width: hw * 0.7 + th, depth: hd * 0.7 + th, offsetZ: -hd * 0.1, squareness: 0.05 },
          ],
          capBottom: false,
        }),
        c.hair,
      ),
    );
  }

  if (style.quiff) {
    // Zijplukken: twee naar achteren gekamde wiggen boven de slapen breken de helm-lijn.
    for (const side of SIDES_LR) {
      const sweep = wedge({
        backWidth: hw * 0.34,
        frontWidth: hw * 0.2,
        height: th * 1.2,
        depth: hd * 0.5,
        backHeight: th * 1.2,
        frontHeight: th * 0.6,
      });
      sweep.rotateZ(side * -0.5);
      sweep.rotateX(-0.2);
      sweep.translate(side * hw * 0.36, hairline.y + th * 0.9, ringFrontZ(hairline, 0) - hd * 0.3);
      parts.push(part(sweep, c.hair));
    }
    // Kuif: naar achteren gekamde massa die net boven de kap uitkomt — het silhouetdetail
    // dat een slickback van bovenaf herkenbaar maakt.
    const quiff = wedge({
      backWidth: hw * 0.78,
      frontWidth: hw * 0.56,
      height: th * 1.5,
      depth: hd * 0.38,
      backHeight: th * 1.5,
      frontHeight: th * 0.8,
    });
    quiff.rotateX(0.32);
    quiff.translate(0, hairline.y + th * 1.0, ringFrontZ(hairline, 0) - hd * 0.13);
    parts.push(part(quiff, c.hair));
  }

  return parts;
}

/** Hoofddeksels. Elk zit op `HAT_BASE` van de hoofdhoogte en volgt de schedel plus haar. */
function hatParts(spec: CharacterSpec, skull: readonly FacetedLoftRing[], faceY: (f: number) => number): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const parts: Part[] = [];
  const hw = p.headWidth;
  const hd = p.headDepth;
  const hh = p.crownHeight - p.chinHeight;
  const base = faceY(HAT_BASE);
  const skullAt = (fraction: number) => ringAt(skull, faceY(fraction));
  const over = p.hairThickness * 0.6 + 0.006;
  const seat = inflate(skullAt(HAT_BASE), over);
  const crownTop = faceY(1) + p.hairThickness;

  switch (spec.features.hat) {
    case 'fedora': {
      const brim = facetedLoft({
        sides: FACETS.hat,
        rings: [
          { y: base - 0.006, width: hw * 1.85, depth: hd * 1.72, offsetZ: hd * 0.06, squareness: 0.12 },
          { y: base + 0.006, width: hw * 1.85, depth: hd * 1.72, offsetZ: hd * 0.06, squareness: 0.12 },
        ],
      });
      brim.rotateX(0.09);
      brim.translate(0, 0.004, 0);
      parts.push(part(brim, c.hat));
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...seat, y: base - 0.002, squareness: 0.3 },
              { ...inflate(seat, 0.004), y: base + 0.05, squareness: 0.32 },
              { ...inflate(seat, -0.006), y: crownTop + 0.045, offsetZ: (seat.offsetZ ?? 0) - 0.01, squareness: 0.5 },
              { y: crownTop + 0.06, width: seat.width * 0.42, depth: seat.depth * 0.78, offsetZ: (seat.offsetZ ?? 0) - 0.012, squareness: 0.3 },
            ],
            capBottom: false,
          }),
          c.hat,
        ),
      );
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...inflate(seat, 0.006), y: base + 0.012, squareness: 0.3 },
              { ...inflate(seat, 0.006), y: base + 0.042, squareness: 0.3 },
            ],
            capBottom: false,
            capTop: false,
          }),
          c.accent === 'gold' ? 'charcoal' : c.accent,
        ),
      );
      break;
    }
    case 'peakedCap': {
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...seat, y: base - 0.004, squareness: 0.3 },
              { ...inflate(seat, 0.002), y: base + 0.042, squareness: 0.3 },
            ],
            capBottom: false,
            capTop: false,
          }),
          c.hat,
          LINING,
        ),
      );
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...inflate(seat, 0.002), y: base + 0.04, squareness: 0.3 },
              { ...inflate(seat, 0.022), y: base + 0.078, offsetZ: (seat.offsetZ ?? 0) + hd * 0.08, squareness: 0.25 },
              { ...inflate(seat, 0.018), y: base + 0.092, offsetZ: (seat.offsetZ ?? 0) + hd * 0.08, squareness: 0.25 },
            ],
            capBottom: false,
          }),
          c.hat,
        ),
      );
      const visor = wedge({ backWidth: hw * 1.0, frontWidth: hw * 0.66, height: 0.01, depth: hd * 0.38, frontHeight: 0.008 });
      visor.rotateX(0.22);
      visor.translate(0, base - 0.008, ringFrontZ(seat, 0) + hd * 0.12);
      parts.push(part(visor, 'rubberBlack'));
      parts.push(part(box(hw * 0.9, 0.008, 0.008).translate(0, base + 0.004, ringFrontZ(seat, 0) + 0.003), 'rubberBlack'));
      const badge = facetedDisc(0.026, 0.03, 6);
      badge.rotateX(Math.PI / 2);
      badge.translate(0, base + 0.03, ringFrontZ(seat, 0) + 0.006);
      parts.push(part(badge, c.accent));
      break;
    }
    case 'flatCap': {
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...inflate(seat, 0.004), y: base - 0.01, squareness: 0.3 },
              { ...inflate(seat, 0.014), y: base + 0.03, offsetZ: (seat.offsetZ ?? 0) + hd * 0.03, squareness: 0.3 },
              { ...inflate(seat, 0.0), y: base + 0.065, offsetZ: (seat.offsetZ ?? 0) + hd * 0.1, squareness: 0.3 },
              { y: base + 0.08, width: seat.width * 0.5, depth: seat.depth * 0.5, offsetZ: (seat.offsetZ ?? 0) + hd * 0.18, squareness: 0.2 },
            ],
            capBottom: false,
          }),
          c.hat,
        ),
      );
      const brim = wedge({ backWidth: hw * 0.95, frontWidth: hw * 0.7, height: 0.01, depth: hd * 0.28, frontHeight: 0.008 });
      brim.rotateX(0.12);
      brim.translate(0, base - 0.008, ringFrontZ(seat, 0) + hd * 0.09);
      parts.push(part(brim, c.hat, LINING));
      break;
    }
    case 'watchCap': {
      // Gebreide muts: volgt de schedel strak tot boven het oor, met een omgeslagen band.
      const low = inflate(skullAt(0.66), over + 0.002);
      const knit = (fraction: number, extra: number): FacetedLoftRing => ({
        ...inflate(skullAt(fraction), over + extra),
        squareness: 0.25,
      });
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...low, y: faceY(0.66), squareness: 0.25 },
              knit(0.82, 0.004),
              knit(0.94, 0.006),
              { y: crownTop + 0.012, width: seat.width * 0.62, depth: seat.depth * 0.64, offsetZ: (seat.offsetZ ?? 0) - 0.01, squareness: 0.15 },
              { y: crownTop + 0.026, width: seat.width * 0.22, depth: seat.depth * 0.24, offsetZ: (seat.offsetZ ?? 0) - 0.014, squareness: 0.1 },
            ],
            capBottom: false,
          }),
          c.hat,
        ),
      );
      parts.push(
        part(
          facetedLoft({
            sides: FACETS.hat,
            rings: [
              { ...inflate(low, 0.008), y: faceY(0.66) - 0.004, squareness: 0.25 },
              { ...inflate(skullAt(0.78), over + 0.012), y: faceY(0.78), squareness: 0.25 },
            ],
            capTop: false,
          }),
          c.hat,
          WORN,
        ),
      );
      break;
    }
    case 'none':
      break;
  }

  void hh;
  return parts;
}

// ---------------------------------------------------------------------------
// Armen
// ---------------------------------------------------------------------------

function upperArmParts(spec: CharacterSpec, side: Side): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const coat = COATS[spec.features.coatStyle];
  const t = p.armThickness;
  const ct = coat.thickness * 2;
  const length = p.shoulderHeight - p.elbowHeight;
  const parts: Part[] = [];

  // Mouw: rond onder de deltoïde, licht plat aan de zijkant, smaller naar de elleboog.
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.limb,
        rings: [
          { y: 0.035, width: t * 1.18 + ct, depth: t * 1.26 + ct, offsetX: side * 0.004, squareness: 0.3 },
          { y: -length * 0.4, width: t * 1.08 + ct, depth: t * 1.16 + ct, offsetX: side * 0.003, squareness: 0.3 },
          // Stofophoping net boven de elleboog: de mouw knikt, hij loopt niet als buis door.
          { y: -length + 0.055, width: t * 0.98 + ct, depth: t * 1.08 + ct, offsetX: side * REST.elbowOut, offsetZ: -0.006, squareness: 0.35 },
          { y: -length + 0.015, width: t * 0.88 + ct, depth: t * 0.96 + ct, offsetX: side * REST.elbowOut, offsetZ: 0.004, squareness: 0.3 },
        ],
      }),
      c.coat,
    ),
  );

  // Ellebooggkogel op de ouderkant: blijft het gewricht dekken als de onderarm draait.
  parts.push(part(jointBall(t * 0.82 + ct * 0.5, -length), c.coat));
  return parts;
}

function foreArmParts(spec: CharacterSpec, side: Side): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const coat = COATS[spec.features.coatStyle];
  const t = p.armThickness;
  const ct = coat.thickness * 2;
  const length = p.elbowHeight - p.wristHeight;
  const parts: Part[] = [];
  const wristW = t * 0.62;
  const drift = REST.forearmDrift;

  parts.push(
    part(
      facetedLoft({
        sides: FACETS.limb,
        rings: [
          { y: 0.0, width: t * 0.88 + ct, depth: t * 0.98 + ct, squareness: 0.3 },
          { y: -length * 0.35, width: t * 0.86 + ct, depth: t * 0.9 + ct, offsetZ: drift * 0.4, squareness: 0.3 },
          { y: -length + 0.035, width: wristW + ct * 0.8, depth: wristW * 1.1 + ct * 0.8, offsetZ: drift, squareness: 0.35 },
        ],
      }),
      c.coat,
    ),
  );

  if (coat.ribbedCuffs) {
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.limb,
          rings: [
            { y: -length + 0.05, width: wristW * 1.22, depth: wristW * 1.34, offsetZ: drift, squareness: 0.4 },
            { y: -length + 0.004, width: wristW * 1.16, depth: wristW * 1.28, offsetZ: drift, squareness: 0.4 },
          ],
        }),
        c.coat,
        LINING,
      ),
    );
  } else {
    // Shirtmanchet die onder de mouw uitkomt.
    parts.push(
      part(
        facetedLoft({
          sides: FACETS.limb,
          rings: [
            { y: -length + 0.036, width: wristW * 1.12, depth: wristW * 1.22, offsetZ: drift, squareness: 0.35 },
            { y: -length + 0.002, width: wristW * 1.08, depth: wristW * 1.18, offsetZ: drift, squareness: 0.35 },
          ],
        }),
        c.shirt,
      ),
    );
  }

  // --- Hand ------------------------------------------------------------------
  // Handpalm plat tegen het dijbeen (breedte langs Z), vier licht gekromde vingers, duim
  // aan de voorkant. Op 200 px is de hand een lichte vlek onder de mouw; van dichtbij
  // moet die vlek als hand lezen en niet als want.
  const hwid = p.handWidth;
  const palmLen = p.handLength * 0.5;
  const wristY = -length;
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.hand,
        rings: [
          { y: wristY + 0.012, width: hwid * 0.34, depth: hwid * 0.74, offsetZ: drift, squareness: 0.5 },
          { y: wristY - palmLen * 0.45, width: hwid * 0.4, depth: hwid * 1.0, offsetZ: drift + hwid * 0.06, squareness: 0.6 },
          { y: wristY - palmLen, width: hwid * 0.36, depth: hwid * 0.98, offsetZ: drift + hwid * 0.08, squareness: 0.6 },
        ],
      }),
      c.skin,
    ),
  );

  const fingerLen = p.handLength * 0.4;
  for (let i = 0; i < 4; i++) {
    const z = drift + hwid * 0.08 + (i - 1.5) * hwid * 0.24;
    const len = fingerLen * (1 - Math.abs(i - 1.4) * 0.08);
    const finger = taperedBox({
      bottomWidth: hwid * 0.2,
      topWidth: hwid * 0.23,
      height: len,
      bottomDepth: hwid * 0.19,
      topDepth: hwid * 0.23,
    });
    finger.translate(0, -len / 2, 0);
    finger.rotateZ(-side * 0.42);
    finger.translate(side * hwid * 0.08, wristY - palmLen + 0.006, z);
    parts.push(part(finger, c.skin));
  }
  const thumbLen = p.handLength * 0.3;
  const thumb = taperedBox({ bottomWidth: hwid * 0.22, topWidth: hwid * 0.25, height: thumbLen, bottomDepth: hwid * 0.2, topDepth: hwid * 0.24 });
  thumb.translate(0, -thumbLen / 2, 0);
  thumb.rotateX(-0.85);
  thumb.rotateZ(side * 0.25);
  thumb.translate(-side * hwid * 0.06, wristY - palmLen * 0.28, drift + hwid * 0.5);
  parts.push(part(thumb, c.skin));

  // Gouden horloge om de rechterpols (side −1) — de enige bewuste asymmetrie in het model.
  if (spec.features.watch && side === -1) {
    parts.push(part(cylinderN(wristW * 0.78, 0.022, 12).translate(0, wristY + 0.01, drift), c.accent));
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Benen
// ---------------------------------------------------------------------------

function thighParts(spec: CharacterSpec, side: Side): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const t = p.legThickness;
  const length = p.hipHeight - p.kneeHeight;
  const parts: Part[] = [];

  parts.push(
    part(
      facetedLoft({
        sides: FACETS.limb,
        rings: [
          { y: 0.08, width: t * 1.12, depth: t * 1.1, offsetZ: 0.004, squareness: 0.35 },
          { y: -length * 0.36, width: t * 1.06, depth: t * 1.04, offsetX: side * 0.003, squareness: 0.35 },
          // Broekspijp bolt net boven de knie op: de stof valt, hij taps niet als kegel.
          { y: -length + 0.075, width: t * 0.94, depth: t * 0.96, offsetZ: 0.006, squareness: 0.4 },
          { y: -length + 0.025, width: t * 0.84, depth: t * 0.86, squareness: 0.3 },
        ],
      }),
      c.trousers,
    ),
  );
  parts.push(part(jointBall(t * 0.76, -length), c.trousers));
  return parts;
}

function shinParts(spec: CharacterSpec, side: Side): Part[] {
  const p = spec.proportions;
  const c = spec.colors;
  const t = p.legThickness;
  const length = p.kneeHeight - p.ankleHeight;
  const parts: Part[] = [];
  void side;

  // Onderbeen: kuit bolt naar achteren, broekspijp valt iets wijder over de schoen.
  parts.push(
    part(
      facetedLoft({
        sides: FACETS.limb,
        rings: [
          { y: 0.03, width: t * 0.86, depth: t * 0.9, squareness: 0.3 },
          { y: -0.06, width: t * 0.9, depth: t * 0.94, offsetZ: 0.004, squareness: 0.35 },
          { y: -length * 0.34, width: t * 0.9, depth: t * 0.98, offsetZ: -0.008, squareness: 0.3, frontBias: 0.42 },
          { y: -length * 0.72, width: t * 0.72, depth: t * 0.76, squareness: 0.35 },
          // Omslag: de pijp breekt over de schoen en valt iets wijder uit.
          { y: -length + 0.04, width: t * 0.74, depth: t * 0.78, offsetZ: 0.004, squareness: 0.45 },
          { y: -length + 0.01, width: t * 0.8, depth: t * 0.84, offsetZ: 0.006, squareness: 0.5 },
        ],
      }),
      c.trousers,
    ),
  );

  // --- Schoen ----------------------------------------------------------------
  // Loft langs de voetas: opgebouwd langs Y en gedraaid naar +Z, zodat elke ring een
  // dwarsdoorsnede van de schoen is (hiel hoog en smal, wreef, bal breed, neus laag).
  const bottom = -length - p.ankleHeight;
  const fl = p.footLength;
  const fw = p.footWidth;
  const ah = p.ankleHeight;
  const section = (forward: number, width: number, height: number, squareness: number): FacetedLoftRing => ({
    y: forward,
    width,
    depth: height,
    offsetZ: -(bottom + height / 2),
    squareness,
  });
  const shoe = facetedLoft({
    sides: FACETS.shoe,
    rings: [
      section(-fl * 0.32, fw * 0.74, ah * 0.95, 0.5),
      section(-fl * 0.16, fw * 0.96, ah * 1.0, 0.6),
      section(fl * 0.12, fw * 1.0, ah * 0.82, 0.6),
      section(fl * 0.42, fw * 0.96, ah * 0.55, 0.7),
      section(fl * 0.64, fw * 0.66, ah * 0.28, 0.6),
    ],
  });
  shoe.rotateX(Math.PI / 2);
  parts.push(part(shoe, c.shoes));
  parts.push(part(box(fw * 1.02, 0.014, fl * 0.97).translate(0, bottom + 0.007, fl * 0.16), 'charcoal'));

  return parts;
}

/** Gewrichtskogel rond een pivot op hoogte `y`: dekt het gat bij het roteren van het kind. */
function jointBall(diameter: number, y: number): THREE.BufferGeometry {
  const r = diameter / 2;
  return facetedLoft({
    sides: FACETS.joint,
    rings: [
      { y: y - r * 0.9, width: diameter * 0.45, depth: diameter * 0.45 },
      { y: y - r * 0.45, width: diameter * 0.9, depth: diameter * 0.9 },
      { y: y + r * 0.45, width: diameter * 0.9, depth: diameter * 0.9 },
      { y: y + r * 0.9, width: diameter * 0.45, depth: diameter * 0.45 },
    ],
  });
}
